import { DEFAULT_THRESHOLDS } from "./constants";
import type {
  CombinedWindow,
  FeatureContribution,
  ModelArtifact,
  RuleFinding,
  RuleThresholds,
} from "../types";

export interface ModelScore {
  error: number;
  threshold: number;
  ratio: number;
  isAnomaly: boolean;
  topFeatures: FeatureContribution[];
}

export function evaluateRules(
  sample: CombinedWindow,
  thresholds: RuleThresholds = DEFAULT_THRESHOLDS,
): RuleFinding[] {
  const findings: RuleFinding[] = [];
  const failures = Number(sample.event_authentication_failure_count ?? 0);
  const successes = Number(sample.event_authentication_success_count ?? 0);
  const packageChanges =
    Number(sample.event_package_installed_count ?? 0) +
    Number(sample.event_package_removed_count ?? 0) +
    Number(sample.event_package_updated_count ?? 0);
  const starts = Number(sample.event_process_started_count ?? 0);
  const elevations = Number(sample.event_privilege_elevation_count ?? 0);

  if (failures >= thresholds.authentication_failure_burst) {
    findings.push({
      rule_id: "authentication_failure_burst",
      severity: successes ? "high" : "medium",
      ratio_floor: successes ? 2.5 : 1,
      description: `${failures} failed logins in one minute${successes ? " with a successful login" : ""}`,
    });
  }
  if (packageChanges >= thresholds.package_change_burst) {
    findings.push({
      rule_id: "package_change_burst",
      severity: "medium",
      ratio_floor: 1,
      description: `${packageChanges} package changes in one minute`,
    });
  }
  if (starts >= thresholds.process_start_burst) {
    findings.push({
      rule_id: "process_start_burst",
      severity: "high",
      ratio_floor: 2.5,
      description: `${starts} processes started in one minute`,
    });
  }
  if (elevations >= thresholds.privilege_elevation_burst) {
    findings.push({
      rule_id: "privilege_elevation_burst",
      severity: "medium",
      ratio_floor: 1,
      description: `${elevations} privilege elevations in one minute`,
    });
  }
  return findings;
}

export function scoreModel(model: ModelArtifact, sample: CombinedWindow): ModelScore {
  validateModel(model);
  const scaled = model.feature_names.map((name, index) => {
    const value = Number(sample[name] ?? 0);
    const std = Math.max(Math.abs(Number(model.std[index] ?? 1)), 1e-9);
    return (value - Number(model.mean[index] ?? 0)) / std;
  });
  const hidden = model.w1.map((weights, index) => {
    const value = weights.reduce((total, weight, feature) => total + weight * scaled[feature], model.b1[index]);
    return Math.tanh(value);
  });
  const output = model.w2.map((weights, index) =>
    weights.reduce((total, weight, hiddenIndex) => total + weight * hidden[hiddenIndex], model.b2[index]),
  );
  const contributions = output.map((value, index) => ({
    feature: model.feature_names[index],
    contribution: (value - scaled[index]) ** 2,
  }));
  const error = contributions.reduce((total, item) => total + item.contribution, 0) / contributions.length;
  const threshold = Math.max(Number(model.threshold), 1e-9);
  return {
    error,
    threshold,
    ratio: error / threshold,
    isAnomaly: error / threshold >= 1,
    topFeatures: contributions.sort((a, b) => b.contribution - a.contribution).slice(0, 5),
  };
}

export function validateModel(model: ModelArtifact): void {
  const size = model.feature_names?.length ?? 0;
  if (!size || model.mean.length !== size || model.std.length !== size) {
    throw new Error("Model feature scaler dimensions do not match");
  }
  if (model.w1.length !== model.hidden_dim || model.b1.length !== model.hidden_dim) {
    throw new Error("Model encoder dimensions do not match");
  }
  if (model.w1.some((row) => row.length !== size)) throw new Error("Model encoder width is invalid");
  if (model.w2.length !== size || model.b2.length !== size) {
    throw new Error("Model decoder dimensions do not match");
  }
  if (model.w2.some((row) => row.length !== model.hidden_dim)) {
    throw new Error("Model decoder width is invalid");
  }
  if (!Number.isFinite(model.threshold) || model.threshold <= 0) {
    throw new Error("Model threshold must be positive");
  }
}
